import { useState, useEffect, useCallback, useMemo } from 'react';
import { Project, Article, LifeMetric, SiteControls, SocialChannel, AboutData } from '../types/content';
import {
  getProjects,
  getArticles,
  getSocialChannels,
  getLifeMetrics,
  getAboutData,
  getSiteControls,
  trackVisitorSession,
  sendAdminUpdate,
  saveProjectsOverride,
  saveArticlesOverride,
  saveSocialOverride,
  saveLifeMetricsOverride,
  saveAboutOverride,
  CONTENT_CHANGE_EVENT,
} from '../services/contentApi';
import {
  seedProjects,
  seedArticles,
  seedSocialChannels,
  seedLifeMetrics,
  seedAboutData,
  defaultSiteControls,
} from '../data/seed';

export function useProjects() {
  const [allProjects, setAllProjects] = useState<Project[]>(seedProjects);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLive, setIsLive] = useState<boolean>(false);

  const loadData = useCallback(() => {
    getProjects().then(({ data, isLive: live }) => {
      setAllProjects(data);
      setIsLive(live);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const toggleProjectVisibility = useCallback((slug: string) => {
    setAllProjects((prev) => {
      const next = prev.map((p) => (p.slug === slug ? { ...p, visible: !p.visible } : p));
      saveProjectsOverride(next);
      return next;
    });
  }, []);

  const projects = useMemo(() => allProjects.filter((p) => p.visible), [allProjects]);

  return { projects, allProjects, toggleProjectVisibility, loading, isLive };
}

export function useArticles() {
  const [allArticles, setAllArticles] = useState<Article[]>(seedArticles);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLive, setIsLive] = useState<boolean>(false);

  const loadData = useCallback(() => {
    getArticles().then(({ data, isLive: live }) => {
      setAllArticles(data);
      setIsLive(live);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const toggleArticleVisibility = useCallback((slug: string) => {
    setAllArticles((prev) => {
      const next = prev.map((a) => (a.slug === slug ? { ...a, published: !a.published } : a));
      saveArticlesOverride(next);
      return next;
    });
  }, []);

  const articles = useMemo(() => allArticles.filter((a) => a.published), [allArticles]);

  return { articles, allArticles, toggleArticleVisibility, loading, isLive };
}

export function useSocialChannels() {
  const [allChannels, setAllChannels] = useState<SocialChannel[]>(seedSocialChannels);
  const [loading, setLoading] = useState<boolean>(true);

  const loadData = useCallback(() => {
    getSocialChannels().then(({ data }) => {
      setAllChannels(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const toggleSocialVisibility = useCallback((id: string) => {
    setAllChannels((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c));
      saveSocialOverride(next);
      return next;
    });
  }, []);

  const channels = useMemo(() => allChannels.filter((c) => c.visible), [allChannels]);

  return { channels, allChannels, toggleSocialVisibility, loading };
}

export function useLifeMetrics() {
  const [allMetrics, setAllMetrics] = useState<LifeMetric[]>(seedLifeMetrics);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLive, setIsLive] = useState<boolean>(false);

  const loadData = useCallback(() => {
    getLifeMetrics().then(({ data, isLive: live }) => {
      setAllMetrics(data);
      setIsLive(live);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const toggleMetricVisibility = useCallback((idOrKey: string | number) => {
    setAllMetrics((prev) => {
      const next = prev.map((m) => {
        const matches = m.id === idOrKey || `${m.month}-${m.metric}` === idOrKey;
        return matches ? { ...m, visible: !m.visible } : m;
      });
      saveLifeMetricsOverride(next);
      return next;
    });
  }, []);

  const metrics = useMemo(() => allMetrics.filter((m) => m.visible), [allMetrics]);

  return { metrics, allMetrics, toggleMetricVisibility, loading, isLive };
}

export function useAboutData() {
  const [about, setAbout] = useState<AboutData>(seedAboutData);
  const [loading, setLoading] = useState<boolean>(true);

  const loadData = useCallback(() => {
    getAboutData().then((data) => {
      setAbout(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const updateAbout = useCallback((patch: Partial<AboutData>) => {
    setAbout((prev) => {
      const next = { ...prev, ...patch };
      saveAboutOverride(next);
      return next;
    });
  }, []);

  return { about, updateAbout, loading };
}

export function useSiteControls() {
  const [controls, setControls] = useState<SiteControls>(defaultSiteControls);
  const [loading, setLoading] = useState<boolean>(true);

  const loadData = useCallback(() => {
    getSiteControls().then((data) => {
      setControls(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener(CONTENT_CHANGE_EVENT, loadData);
    return () => window.removeEventListener(CONTENT_CHANGE_EVENT, loadData);
  }, [loadData]);

  const updateControls = useCallback((patch: Partial<SiteControls>) => {
    setControls((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem('shvms_site_controls', JSON.stringify(next));
        window.dispatchEvent(new Event(CONTENT_CHANGE_EVENT));
      } catch {}
      return next;
    });

    sendAdminUpdate('site_controls', 'PATCH', patch);
  }, []);

  return { controls, loading, updateControls };
}

export function useVisitorCounter() {
  const [visitorCount, setVisitorCount] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;
    trackVisitorSession().then((count) => {
      if (mounted && count !== null) {
        setVisitorCount(count);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return visitorCount;
}
